/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useState, SetStateAction } from 'react';
import {
	Alignment,
	Button,
	Icon,
	InputGroup,
	Intent,
	Popover,
	Position,
	Tooltip,
	Spinner,
	Checkbox,
	MenuItem,
	PopoverInteractionKind,
	FormGroup,
	Switch
} from '@blueprintjs/core';
import { Dropdown } from '../../components/dropdowns/dropdown';
import { pushUpdatedDefinition } from './utils';
import { MultiSelect } from '@blueprintjs/select';

import { ColorProps, PropertyListProps, DefinitionProps, PropertyProps } from './interfaces';
import { PropertyFlag, PropertyType } from 'srs-middleware';
import { DatasetManagementContext } from "./datasetManagementContext"

export const SchemaEditor: React.FC<SchemaEditorProps> = ({
	dil,
	setDil,
	colors,
	onComplete,
	additionalfooterContent: footerElements,
}) => {
	const [definitionPropertyList, setDefinitionPropertyList] = useState<PropertyProps[]>([]);
	const [definitionName, setDefinitionName] = useState<string>();
	const [isShared, setIsShared] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);

	const {toaster, setNeedUpdate, setUnsavedChanges} = React.useContext(DatasetManagementContext);

	const setInitValues = () => {
		if (!dil) {
			console.error('No definition available.')
		}
		const initialPropList: PropertyProps[] = Object.keys(dil.propertyList).map((prop) => { 
			return dil.propertyList[prop] as PropertyProps;
		}).sort((a: PropertyProps, b: PropertyProps) => a['index'] - b['index']);
		setDefinitionPropertyList(initialPropList);
        setDefinitionName(dil.name);
        setIsShared(dil.shared)
	}

	React.useEffect(() => {
		setInitValues(); 
	}, [dil]);

	const cancel = () => {
		window.location.reload(false);  
	}

	const updateDIL = React.useCallback(async () => {
		// convert definitionPropertyList back to object
		let updatedPropertyList: PropertyListProps = {};
		definitionPropertyList.forEach((property) => {
			if (property.include == false) return; 
			if (property.flags.includes(PropertyFlag.Hierarchical) && property.delimiter === undefined) {
				property.delimiter = '/';
			}
			if (property.flags.includes(PropertyFlag.Multivalued) && property.multivaluedDelimiter === undefined) {
				property.multivaluedDelimiter = ',';
			}
			updatedPropertyList[property.name] = { ...property };	
		});

		// Checks to see if there has been an update from original definition
		if (updatedPropertyList !== dil.propertyList || definitionName !== dil.name || isShared !== dil.shared) {
			try {
				setLoading(true);
				const definition: DefinitionProps = {
					...(dil as DefinitionProps),
					name: definitionName,
					propertyList: { ...updatedPropertyList },
					shared: isShared,
				};
				await pushUpdatedDefinition(definition);
				setDil(definition);
				onComplete();
                setLoading(false);
			} catch (err) {
                toaster.show({
                    icon: 'warning-sign',
                    message: 'Server Error: ' + err.message,
                    intent: Intent.DANGER,
                });
				setLoading(false);
			}
		}
		setNeedUpdate(true);
		setUnsavedChanges(false); 
	}, [definitionName, definitionPropertyList, dil, onComplete, setDil, toaster, isShared]);

	// criteria to make a property name valid, returns true if valid
	const verifyPropertyName = React.useCallback(
		(name: string) =>
			definitionPropertyList.filter((property) => property.name === name).length === 1 && name.length > 1,
		[definitionPropertyList]
	);

	const updateProperty = React.useCallback(
		(property_index: number, key: string, change: any) => {
			let properties = definitionPropertyList;
			properties.find((property) => property.index === property_index)[key] = change;
			setDefinitionPropertyList([...properties]);
			setUnsavedChanges(true); 
	}, [definitionPropertyList]);

	const updateSharing = (e) => { 
		setIsShared(!isShared); 
		setUnsavedChanges(true); 
	}

	const updateDilName = (e: React.FormEvent<HTMLInputElement>) => {
		setDefinitionName(e.currentTarget.value);
		setUnsavedChanges(true); 
	}

	const headers: HeaderProps[] = [
		{ width: '7%', name: 'Sample', infoDetails: 'Sample values extracted from the data.' },
		{ width: '7%', name: 'Include', infoDetails: 'Indicates if this property should be included in the schema.' },
		{ width: '15%', name: 'Property', infoDetails: 'Name for this property. Initialized with the column header.' },
		{
			width: '10%',
			name: 'Type',
			infoDetails:
				"The property type that these values represent. These affect how the system interprets the values and what analytics can be performed. \n Text types represent a block of unstructured text; for example, the body of text from a news story where String types represent a block of structured text that is valid only in its entirety; 'New York Times'",
		},
		{
			width: '30%',
			name: 'Flags',
			infoDetails:
				'Flag modifiers provide additional information as to how each property can be used or how the values should be parsed.',
		},
		{
			width: '10%',
			name: 'Hierarchical Delimiter',
			infoDetails: 'For values that represent a hierarchical path, this is the delimiter used to describe the path.',
		},
		{
			width: '10%',
			name: 'Multivalued Delimiter',
			infoDetails:
				'For values that represent multiple values joined together, this is the delimiter used to join the values.',
		},
	];

	return (
		<div css={editorContainerStyle}>
			<div css={metadataContainerCss}>
				<Switch checked={isShared} large={true} label="Shared Dataset?" onChange={updateSharing} inline={true}/>

				<FormGroup label="Name" inline={true}>
					<InputGroup css={dilNameInputCss} defaultValue={definitionName} onChange={updateDilName} placeholder="Name" />
				</FormGroup>
			
			</div>
			<br />
            
			<div css={tableContainerStyle}>
				<table css={tableStyle}>
					<thead>
						<tr key="header">
							{headers.map((header: HeaderProps) => (
								<th key={header.name} css={{ width: header.width }}>
									{header.name} {popup(header.infoDetails, 'info', colors.text)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{definitionPropertyList.map((property: PropertyProps) => {
							return <EditorRow
								property={property}
								verifyPropertyName={verifyPropertyName}
								updateProperty={updateProperty}
                                textColor={colors.text}
                                key={`${definitionName}_${property?.index}`}
							/>
						}
							
						)}
					</tbody>
				</table>
			</div>
			<div css={metadataContainerCss}>
				<Button
					css={footerButtonCss}
					icon="saved"
					onClick={updateDIL}
					intent="primary"
					outlined={true}
				>
					Done
				</Button>
				<Button
					css={{ paddingTop: 5, marginBottom: 10 }}
					onClick={cancel}
					intent="primary"
					outlined={true}
				>
					Cancel
				</Button>
			</div>
			<div css={spinnerContainerCss}>{loading && <Spinner />}</div>
		</div>
	);
};

/**
 * One row in the schema editor
 */
const EditorRow: React.FC<EditorRowProps> = ({ property, verifyPropertyName, updateProperty, textColor }) => {
	const { index, flags, name, samples, type, include=true } = property;
	const setName = (e) => updateProperty(index, 'name', e.target.value);
	const setHierarchicalDelimeter = (e) => updateProperty(index, 'delimeter', e.target.value);
	const setMultivaluedDelimeter = (e) => updateProperty(index, 'multiValuedDelimiter', e.target.value);

	const addFlag = (flag) => {
		if (!flags.includes(flag)) {
			updateProperty(index, 'flags', [...flags, flag]);
		}
	};
	const removeFlag = (flag: string) =>
		updateProperty(
			index,
			'flags',
			flags.filter((selected_flag) => selected_flag !== flag)
		);

	const setType = (type: string) => updateProperty(index, 'type', type);
    const setInclude = () => updateProperty(index, 'include', !include ? include !== undefined : false);      	

	return (
		<tr className={!include ? 'disabled' : 'enabled'} key={index}>
			<td key={index + '_sample'}>{popup(samples?.join('\n\n'), 'table', textColor)}</td>
			<td key={index + '_include'}>
				<Checkbox checked={include} onChange={setInclude} />
			</td>
			<td key={index + '_name'}>
				<Tooltip
					content="A property name must be unique and one character or more"
					position={Position.TOP}
					disabled={verifyPropertyName(name)}
				>
					<InputGroup
						disabled={!include}
						defaultValue={name}
						onChange={setName}
						intent={!verifyPropertyName(name) ? Intent.WARNING : Intent.SUCCESS}
						fill={true}
					/>
				</Tooltip>
			</td>
			<td key={name + '_type'}>
				<Dropdown
					activeItem={type}
					disabled={!include}
					label=""
					onSelectionChanged={setType}
					buttonWidth="100px"
					options={Object.values(PropertyType)}
				/>
			</td>
			<td key={name + '_flags'}>
				{include ? (
					<MultiSelect
						tagRenderer={(item) => item}
						itemRenderer={(item, { handleClick, modifiers, query }) => {
							if (modifiers.matchesPredicate) {
								return <MenuItem active={modifiers.active} key={item} text={item} onClick={handleClick} />;
							} else return null;
						}}
						selectedItems={flags}
						items={Object.values(PropertyFlag)}
						noResults={<MenuItem disabled={true} text="No results." />}
						onItemSelect={addFlag}
						onRemove={removeFlag}
						popoverProps={{ minimal: true }}
						fill={true}
						resetOnSelect={true}
						placeholder={'Select Flags'}
						itemPredicate={(query, item, _index) => {
							query = query.toLowerCase();
							item = item.toLowerCase();
							return query === item || item.indexOf(query) >= 0;
						}}
					/>
				) : (
					''
				)}
			</td>
			{flags.includes(PropertyFlag.Hierarchical) ? (
				<td key={name + '_hDelimeter'}>
					<InputGroup
						fill={true}
						css={{ width: '50%', height: 30 }}
						disabled={!include}
						defaultValue={'/'}
						onChange={setHierarchicalDelimeter}
					/>
				</td>
			) : (
				<td key={name + '_hDelimeter'} />
			)}
			{flags.includes(PropertyFlag.Multivalued) ? (
				<td key={index + '_mDelimiter'}>
					<InputGroup
						fill={true}
						css={{ width: '50%', height: 30 }}
						disabled={!include}
						defaultValue={','}
						onChange={setMultivaluedDelimeter}
					/>
				</td>
			) : (
				<td key={name + '_mDelimiter'} />
			)}
		</tr>
	);
};

interface SchemaEditorProps {
	colors: ColorProps;
	dil: DefinitionProps;
	setDil: React.Dispatch<SetStateAction<DefinitionProps>>;
	onComplete: () => any;
	additionalfooterContent?: React.ReactElement;
}

interface HeaderProps {
	width: string;
	name: string;
	infoDetails: string;
}

interface EditorRowProps {
	property: PropertyProps;
	verifyPropertyName: (name: string) => boolean;
	updateProperty: (property_index: number, key: string, change: any) => void;
	textColor: string;
}

const editorContainerStyle = css`
	display: flex;
	flex-direction: column;
	flex: 0 1 auto;
    height: 100%;
	min-height: 0;
`;

const tableContainerStyle = css`
	overflow: auto;
	border-style: solid;
	border-width: 1px;
	border-color: rgba(16, 22, 26, 0.15); 
`;

const tableStyle = css`
	width: 100%;
	border-collapse: separate;
	border-spacing: 0px;

	tr {
		height: 40px;
	}

	.disabled {
		background-color: #b8b8b8;
	}

	.enabled {
		tr:nth-of-type(even) {
			background-color: rgba(191, 204, 214, 0.2);
		}
	}

	td,
	th {
		border: 1px solid rgba(16, 22, 26, 0.15);
		padding: 5px;
	}

	th {
		position: sticky;
		top: 0;
		z-index: 2;
		background: rgba(245, 248, 250);
		color: black;
	}

	td {
		margin: 0 auto;
		text-align: center;
		height: 30px;
	}
`;
/** Singleton toaster instance. Create separate instances for different options. */
const popup = (tooltipContent: string, icon: string, color: string) => (
	<Popover
		position={Position.RIGHT}
		interactionKind={PopoverInteractionKind.HOVER}
		popoverClassName="bp3-popover-content-sizing"
		usePortal={true}
		content={<div css={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tooltipContent}</div>}
		target={
			icon === 'info' ? (
				<Icon icon="info-sign" iconSize={12} css={{ color: color }} />
			) : (
				<Icon icon="th" iconSize={16} css={{ color: color }} />
			)
		}
	/>
);

const dilNameInputCss = css`
	background: #ffffff;
	box-shadow: inset 0 1px 1px 0 rgba(16, 22, 26, 0.2);
	border-radius: 3px;
	border-width: thin;
	width: 277px;
	height: 30px;
`;

const spinnerContainerCss = css`
	width: '100%';
	margin: 'auto';
	align-items: 'center';
	display: 'inline-block';
	justify-content: 'center';
`;

const metadataContainerCss = css`
	display: flex;
	width: 100%;
	flex-direction: row-reverse;
	justify-content: space-between;
	margin-top: 8px;
`;

const footerButtonCss = css`
	float: right;
	padding-topop: 5px;
	margin-bottom: 10px;
`; 
